'use client'
import { AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse } from 'axios'
import http from '../interceptors/http'

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Helper: get token from localStorage (only runs on client)
function getToken(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const local = localStorage.getItem('persist:root')
    if (!local) return null
    const parsed = JSON.parse(local)
    const auth = parsed?.auth ? JSON.parse(parsed.auth) : null
    return auth?.userToken || null
  } catch {
    return null
  }
}

// Retry logic
async function retryRequest<T>(
  requestFn: () => Promise<T>,
  retries = 1,
  delays = [3000, 5000, 8000],
): Promise<T> {
  let lastError
  for (let i = 0; i < retries; i++) {
    try {
      return await requestFn()
    } catch (err) {
      lastError = err
      if (i < retries - 1) await delay(delays[i])
    }
  }
  throw lastError
}

//get file
async function httpGetFile<T>(
  url: string,
  config: AxiosRequestConfig = {},
): Promise<AxiosResponse<T>> {
  const authToken = config.headers?.Authorization || `Bearer ${getToken()}`

  return retryRequest(() =>
    http.get<T>(url, {
      ...config,
      headers: {
        Authorization: authToken,
        ...(config.headers || {}),
      },
    }),
  )
}

// GET
async function httpGet<T>(
  url: string,
  headers?: AxiosRequestHeaders,
  params?: Record<string, unknown>,
  token?: string,
): Promise<T> {
  const authToken = token || getToken()
  return retryRequest(() =>
    http
      .get<T>(url, {
        headers: {
          Authorization: authToken ? `Bearer ${authToken}` : '',
          ...headers,
        },
        params,
      })
      .then((res) => res.data),
  )
}

// POST
async function httpPost<T>(
  url: string,
  payload: unknown,
  headers?: AxiosRequestHeaders,
  token?: string,
): Promise<T> {
  const authToken = token || getToken()
  return retryRequest(() =>
    http
      .post<T>(url, payload, {
        headers: {
          Authorization: authToken ? `Bearer ${authToken}` : '',
          ...headers,
        },
      })
      .then((res) => res.data),
  )
}

// PUT
async function httpPut<T>(
  url: string,
  payload: unknown,
  headers?: AxiosRequestHeaders,
  token?: string,
): Promise<T> {
  const authToken = token || getToken()
  return retryRequest(() =>
    http
      .put<T>(url, payload, {
        headers: {
          Authorization: authToken ? `Bearer ${authToken}` : '',
          ...headers,
        },
      })
      .then((res) => res.data),
  )
}

// DELETE
async function httpDelete<T>(
  url: string,
  headers?: AxiosRequestHeaders,
  token?: string,
): Promise<T> {
  const authToken = token || getToken()
  return retryRequest(() =>
    http
      .delete<T>(url, {
        headers: {
          Authorization: authToken ? `Bearer ${authToken}` : '',
          ...headers,
        },
      })
      .then((res) => res.data),
  )
}

// Construct API endpoint with query parameters
export const constructApiEndpoint = (
  baseUrl: string,
  queryParams: Record<string, string | number | boolean>,
): string => {
  const queryString = Object.entries(queryParams)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&')
  return queryString ? `${baseUrl}?${queryString}` : baseUrl
}

export { httpGet, httpGetFile, httpPost, httpPut, httpDelete }
